'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as echarts from 'echarts/core';
import { BarChart as EBarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { format, subDays, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ChevronDown, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  getProductViewStats,
  type HourlyViewStats,
} from '@/lib/services/statsService';

// Register ECharts modules (tree-shaking)
echarts.use([
  EBarChart,
  GridComponent,
  TooltipComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

// ============================================================================
// MOCK DATA — 60 days of hourly data (used when NEXT_PUBLIC_ENABLE_MOCK_DATA)
// ============================================================================

function generateMockData(): HourlyViewStats[] {
  const points: HourlyViewStats[] = [];
  const now = new Date();
  const totalHours = 60 * 24;

  for (let i = totalHours; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 60 * 60 * 1000);
    const hour = date.getHours();

    const nightDip = hour >= 0 && hour < 7 ? 0.15 + Math.random() * 0.15 : 1;
    const morningRamp = hour >= 7 && hour < 10 ? 0.4 + (hour - 7) * 0.2 : 1;
    const afternoonPeak =
      hour >= 12 && hour < 16 ? 1.3 + Math.random() * 0.4 : 1;
    const eveningTraffic =
      hour >= 18 && hour < 23 ? 0.9 + Math.random() * 0.3 : 1;

    const dayOfWeek = date.getDay();
    const weekendBoost = dayOfWeek === 0 || dayOfWeek === 6 ? 1.25 : 1;

    const progress = 1 - i / totalHours;
    const growthTrend = 0.6 + progress * 0.8;
    const viralSpike =
      Math.abs(progress - 0.65) < 0.05 ? 2.5 + Math.random() * 1.5 : 1;
    const noise = 0.7 + Math.random() * 0.6;

    const baseViews = 140;
    const viewCount = Math.max(
      0,
      Math.round(
        baseViews *
          nightDip *
          morningRamp *
          afternoonPeak *
          eveningTraffic *
          weekendBoost *
          growthTrend *
          viralSpike *
          noise
      )
    );

    points.push({ hour: date.toISOString(), viewCount });
  }

  return points;
}

const USE_MOCK_DATA = process.env.NEXT_PUBLIC_ENABLE_MOCK_DATA === 'true';

// ============================================================================
// TYPES
// ============================================================================

interface ProductStatsProps {
  productSlug: string;
}

// ============================================================================
// ECHARTS WRAPPER — minimal, React 19 compatible
// ============================================================================

function EChart({
  option,
  style,
}: {
  option: Record<string, unknown>;
  style?: React.CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    chartRef.current = echarts.init(containerRef.current);
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: true });
  }, [option]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return <div ref={containerRef} style={style} />;
}

// ============================================================================
// THEME COLORS — resolve CSS custom properties to rgb() for ECharts canvas
// ============================================================================

function useThemeColors() {
  const [colors, setColors] = useState({
    chart: 'rgb(43, 183, 163)',
    fg: 'rgb(30, 29, 46)',
    bg: 'rgb(245, 244, 249)',
    border: 'rgb(224, 223, 232)',
    muted: 'rgb(109, 107, 122)',
  });

  useEffect(() => {
    const resolve = () => {
      const el = document.createElement('div');
      document.body.appendChild(el);

      const get = (v: string) => {
        el.style.color = `var(${v})`;
        return getComputedStyle(el).color;
      };

      const result = {
        chart: get('--chart-2'),
        fg: get('--foreground'),
        bg: get('--background'),
        border: get('--border'),
        muted: get('--muted-foreground'),
      };

      document.body.removeChild(el);
      return result;
    };

    setColors(resolve());

    const observer = new MutationObserver(() => setColors(resolve()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return colors;
}

function rgba(color: string, alpha: number): string {
  if (color.startsWith('rgba')) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
  }
  return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
}

// ============================================================================
// COMPONENT
// ============================================================================

const MS_24H = 24 * 60 * 60 * 1000;

export function ProductStats({ productSlug }: ProductStatsProps) {
  const [data, setData] = useState<HourlyViewStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);

  const colors = useThemeColors();

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (USE_MOCK_DATA) {
      await new Promise((r) => setTimeout(r, 300));
      setData(generateMockData());
      setIsLoading(false);
      return;
    }

    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(subDays(new Date(), 60), 'yyyy-MM-dd');
    const result = await getProductViewStats(productSlug, from, to);

    if (result.success && result.data) {
      setData(result.data.hourly);
    } else {
      setError(result.error?.message || 'Не удалось загрузить');
    }
    setIsLoading(false);
  }, [productSlug]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const stats = useMemo(() => {
    if (!data.length) return { total: 0, avg: 0, peak: 0, peakLabel: '' };

    const total = data.reduce((s, p) => s + p.viewCount, 0);
    const avg = Math.round(total / data.length);

    let peakVal = 0;
    let peakHour = '';
    data.forEach((p) => {
      if (p.viewCount > peakVal) {
        peakVal = p.viewCount;
        peakHour = p.hour;
      }
    });

    const peakLabel = peakHour
      ? format(parseISO(peakHour), 'EE, d MMM, HH:mm', { locale: ru })
      : '';

    return { total, avg, peak: peakVal, peakLabel };
  }, [data]);

  // Raw hourly data as [timestamp_ms, viewCount] pairs for ECharts time axis
  const chartData = useMemo(
    () => data.map((p) => [new Date(p.hour).getTime(), p.viewCount]),
    [data]
  );

  const option = useMemo(() => {
    if (!chartData.length) return {};

    return {
      animation: true,
      animationDuration: 600,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 0,
      grid: {
        top: 8,
        right: 16,
        bottom: 56,
        left: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          fontSize: 11,
          color: colors.muted,
          formatter: (value: number) => {
            const d = new Date(value);
            if (d.getHours() === 0 && d.getMinutes() === 0) {
              return format(d, 'd MMM', { locale: ru });
            }
            return format(d, 'HH:mm');
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: {
            type: [4, 4] as [number, number],
            color: rgba(colors.border, 0.6),
          },
        },
        axisLabel: {
          fontSize: 11,
          color: colors.muted,
          formatter: (v: number) =>
            v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v),
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: rgba(colors.fg, 0.04),
          },
        },
        backgroundColor: colors.fg,
        borderColor: 'transparent',
        borderWidth: 0,
        padding: [8, 12],
        extraCssText:
          'border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);',
        textStyle: {
          color: colors.bg,
          fontSize: 12,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const item = Array.isArray(params) ? params[0] : params;
          if (!item?.data) return '';
          const [ts, views] = item.data as [number, number];
          const d = new Date(ts);
          const dateStr = format(d, 'EE, d MMMM, HH:mm', { locale: ru });
          return `<div style="font-weight:600;font-variant-numeric:tabular-nums">${views.toLocaleString('ru-RU')} просмотров</div><div style="opacity:0.7;margin-top:2px;text-transform:capitalize">${dateStr}</div>`;
        },
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          height: 28,
          bottom: 4,
          minValueSpan: MS_24H,
          borderColor: 'transparent',
          backgroundColor: rgba(colors.border, 0.15),
          fillerColor: rgba(colors.chart, 0.1),
          handleStyle: {
            color: colors.chart,
            borderColor: colors.chart,
            borderWidth: 1,
          },
          handleSize: '60%',
          dataBackground: {
            lineStyle: { color: rgba(colors.chart, 0.25), width: 1 },
            areaStyle: { color: rgba(colors.chart, 0.06) },
          },
          selectedDataBackground: {
            lineStyle: { color: rgba(colors.chart, 0.5), width: 1 },
            areaStyle: { color: rgba(colors.chart, 0.12) },
          },
          textStyle: { color: colors.muted, fontSize: 10 },
          brushSelect: false,
          labelFormatter: (value: number) =>
            format(new Date(value), 'd MMM', { locale: ru }),
        },
        {
          type: 'inside',
          zoomOnMouseWheel: true,
          moveOnMouseMove: false,
          moveOnMouseWheel: false,
          minValueSpan: MS_24H,
        },
      ],
      series: [
        {
          type: 'bar',
          data: chartData,
          itemStyle: {
            color: colors.chart,
            borderRadius: [3, 3, 0, 0],
          },
          barMinWidth: 1,
          barMaxWidth: 32,
          emphasis: {
            itemStyle: {
              color: colors.chart,
              opacity: 0.8,
            },
          },
          large: true,
          largeThreshold: 500,
        },
      ],
    };
  }, [chartData, colors]);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between transition-colors hover:bg-foreground/[0.02]"
      >
        <span className="text-sm font-medium text-foreground">Аналитика</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-foreground-tertiary transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-5">
              {/* Loading */}
              {isLoading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-5 h-5 text-foreground-tertiary animate-spin" />
                </div>
              )}

              {/* Error */}
              {!isLoading && error && (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <p className="text-sm text-foreground-secondary">{error}</p>
                  <Button variant="outline" size="sm" onClick={fetchStats}>
                    Повторить
                  </Button>
                </div>
              )}

              {/* Content */}
              {!isLoading && !error && chartData.length > 0 && (
                <>
                  {/* Stats */}
                  <div className="flex items-baseline gap-8">
                    <div>
                      <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">
                        {stats.total.toLocaleString('ru-RU')}
                      </div>
                      <div className="text-[11px] text-foreground-tertiary mt-0.5">
                        просмотров
                      </div>
                    </div>
                    <div>
                      <div className="text-xl font-semibold tabular-nums text-foreground-secondary">
                        {stats.avg.toLocaleString('ru-RU')}
                      </div>
                      <div className="text-[11px] text-foreground-tertiary mt-0.5">
                        в среднем / час
                      </div>
                    </div>
                    <div>
                      <div className="text-xl font-semibold tabular-nums text-foreground-secondary">
                        {stats.peak.toLocaleString('ru-RU')}
                      </div>
                      <div className="text-[11px] text-foreground-tertiary mt-0.5 capitalize">
                        пик &middot; {stats.peakLabel}
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <EChart
                    option={option}
                    style={{ height: 300, width: '100%' }}
                  />

                  <p className="text-[10px] text-foreground-tertiary text-center -mt-2">
                    Прокрутите для масштабирования &middot; перетащите для
                    навигации
                  </p>
                </>
              )}

              {/* Empty */}
              {!isLoading && !error && chartData.length === 0 && (
                <p className="text-sm text-foreground-tertiary text-center py-12">
                  Нет данных за выбранный период
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
